import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { minPlanFor } from '@dale/plans';
import { isCoach } from '../auth/roles';
import { usePermissions } from '../auth/usePermissions';
import { useSession } from '../auth/useSession';

import { ApiError } from '../api/client';
import { completeBooking, getBookingDetail } from '../api/endpoints/bookings';
import { getBookingBalance } from '../api/endpoints/charges';
import { acknowledgeCue, getBookingCue } from '../api/endpoints/cues';
import { listTables, reassignBookingTable } from '../api/endpoints/tables';
import { createIdempotencyKeyHolder } from '../api/idempotency';
import type { BookingDetail, BookingEvent, BusinessTable } from '../api/schemas';
import { applyBookingToCaches } from '../lib/bookings';
import { BOOKING_BALANCE_QUERY_KEY } from '../lib/bookingPayments';
import { ConfirmAction } from '../components/ConfirmAction';
import { CueBanner } from '../components/CueBanner';
import { CustomerFile } from '../components/CustomerFile';
import { fileVocabulary, profileSummary } from '../lib/customerFile';
import { useCustomerProfiles } from '../lib/useCustomerProfiles';
import { EmptyState } from '../components/EmptyState';
import { ErrorBanner } from '../components/ErrorBanner';
import { LoadingState } from '../components/LoadingState';
import { PageHeader } from '../components/PageHeader';
import { bookingTitle, customerNameOf, staffNameOf } from '../lib/bookingPresentation';
import { BOOKING_STATUS_LABEL, BOOKING_STATUS_TONE } from '../lib/bookings';
import { formatDateTime, formatMoneyCents } from '../lib/format';
import { kindLabel } from '../lib/floorPlan';
import { modeForCategory } from '../lib/mode';
import { meetsPlan } from '../lib/plan';
import { useCapability } from '../lib/useCapability';
import { serviceName, useBusiness } from '../lib/useBusiness';

const ACTOR_LABEL: Record<BookingEvent['actorType'], string> = {
  customer: 'por el cliente',
  business_user: 'por el negocio',
  system: 'por Dale',
};

const SOURCE_LABEL = {
  user: 'Pedida por el cliente',
  proactive: 'Propuesta por Dale',
} as const;

export function BookingDetailPage() {
  const { bookingId } = useParams({ from: '/reservas/$bookingId' });
  const queryClient = useQueryClient();
  const business = useBusiness();
  const profiles = useCustomerProfiles();
  const { session } = useSession();
  const { can } = usePermissions();
  const completeKey = useRef(createIdempotencyKeyHolder()).current;
  const acknowledgeKey = useRef(createIdempotencyKeyHolder()).current;

  const detailQuery = useQuery({
    queryKey: ['business-booking', bookingId],
    queryFn: () => getBookingDetail(bookingId),
  });
  const booking = detailQuery.data;

  const cueQuery = useQuery({
    queryKey: ['booking-cue', bookingId],
    queryFn: () => getBookingCue(bookingId),
    // Customer information: a profile without Clientes is refused it, so it is not asked.
    enabled: booking?.status === 'confirmed' && can('clientes'),
  });
  const cue = cueQuery.data?.cue;

  const acknowledgeMutation = useMutation({
    mutationFn: (cueId: string) => acknowledgeCue(bookingId, cueId, acknowledgeKey.ensure()),
    onSuccess: () => {
      queryClient.setQueryData(['booking-cue', bookingId], { cue: null });
    },
    onSettled: () => acknowledgeKey.reset(),
  });

  const completeMutation = useMutation({
    mutationFn: () => completeBooking(bookingId, completeKey.ensure()),
    onSuccess: (completed) => {
      applyBookingToCaches(queryClient, completed);
    },
    onSettled: () => completeKey.reset(),
  });

  const portalMode = business ? modeForCategory(business.category) : 'services';
  const hasFloor = portalMode !== 'services';
  const floorOpen = useCapability('mesas');
  const managesFloor = hasFloor && can('servicios') && floorOpen;
  const tablesQuery = useQuery({
    queryKey: ['business-tables'],
    queryFn: () => listTables(false),
    enabled: managesFloor,
  });
  const tables = managesFloor ? tablesQuery.data?.data ?? [] : [];
  const currentTable = tables.find((table) => table.id === booking?.tableId);

  // Visits still to charge get a door straight into Cobrar, priced at what the
  // visit still owes. Coaches never run the counter.
  const navigate = useNavigate();
  const unpaidStatus = booking?.status === 'confirmed' || booking?.status === 'completed';
  const balanceQuery = useQuery({
    queryKey: [BOOKING_BALANCE_QUERY_KEY, bookingId],
    queryFn: () => getBookingBalance(bookingId),
    enabled: !isCoach(session) && unpaidStatus && (booking?.amountCents ?? 0) > 0,
  });
  const balance = balanceQuery.data;
  // A credit of the client's package paid the visit: nothing is owed for its service.
  const packCovered = balance?.packCovered === true;
  const charged = !packCovered && Boolean(balance && balance.balanceCents <= 0 && balance.paidCents > 0);
  const canCharge = unpaidStatus && !isCoach(session) && !charged && !packCovered && !balanceQuery.isLoading;
  const dueCents = balance ? balance.balanceCents : booking?.amountCents ?? 0;
  // After an abono the button says it is the saldo, and Cobrar brings only that.
  const dueLabel = dueCents <= 0 ? '' : `${(balance?.paidCents ?? 0) > 0 ? 'el saldo de ' : ''}${formatMoneyCents(dueCents)} `;
  // What the team noted about this client, readable before they walk in; the full ficha sits below the actions.
  const fileSummary = booking?.customerId
    ? profileSummary(profiles.get(booking.customerId), fileVocabulary(business?.category).levels)
    : null;

  const notFound =
    detailQuery.isError &&
    detailQuery.error instanceof ApiError &&
    detailQuery.error.status === 404;

  return (
    <main>
      {booking ? (
        <>
          <PageHeader
            title={bookingTitle(booking, business)}
            description={formatDateTime(booking.startsAt)}
          />
          <section aria-label="Detalles" className="section">
            {managesFloor && tablesQuery.isError ? <ErrorBanner message="No pudimos cargar los lugares." /> : null}
            <div className="rows">
              <FactRow label="Estado">
                <span className="status" data-tone={BOOKING_STATUS_TONE[booking.status]}>
                  {BOOKING_STATUS_LABEL[booking.status]}
                </span>
              </FactRow>
              {bookingFactRows(booking, business)}
              {currentTable ? <FactRow label={kindLabel(currentTable.kind)}>{currentTable.name}</FactRow> : null}
              {booking.amountCents > 0 ? (
                <FactRow label="Monto">
                  <span className="amount">{formatMoneyCents(booking.amountCents)}</span>
                </FactRow>
              ) : null}
              <FactRow label="Origen">{SOURCE_LABEL[booking.source]}</FactRow>
            </div>
          </section>
          {booking.customerPhone ? (
            // Without a phone there is nothing to act on here, and the name
            // already leads the page.
            <section aria-label="Cliente" className="section">
              <h2 className="section-label">Cliente</h2>
              <div className="row">
                <div className="grow">
                  <p className="row-title">{customerNameOf(booking) ?? 'Cliente Dale'}</p>
                  <p className="row-sub">{booking.customerPhone}</p>
                  {fileSummary ? <p className="row-sub client-level">{fileSummary}</p> : null}
                </div>
                <a className="btn small ghost" href={`tel:${booking.customerPhone}`}>
                  Llamar
                </a>
              </div>
            </section>
          ) : null}
          {canCharge ? (
            <section aria-label="Cobro" className="section">
              <button
                type="button"
                className="btn booking-charge"
                onClick={() => void navigate({ to: '/cobrar', search: { reserva: bookingId } })}
              >
                Cobrar {dueLabel}a {customerNameOf(booking)?.split(' ')[0] ?? 'este cliente'}
              </button>
            </section>
          ) : null}
          {charged ? (
            <section aria-label="Cobro" className="section">
              <p className="status" data-tone="good">
                Cobrada {formatMoneyCents(balance?.paidCents ?? 0)}
              </p>
            </section>
          ) : null}
          {packCovered ? (
            <section aria-label="Cobro" className="section">
              <p className="status" data-tone="good">
                Pagada con paquete
              </p>
            </section>
          ) : null}
          {cue || booking.status === 'confirmed' ? (
            <section aria-label="Acciones" className="section">
              {cue ? (
                <CueBanner
                  cue={cue}
                  onAcknowledge={() => acknowledgeMutation.mutate(cue.cueId)}
                  acknowledging={acknowledgeMutation.isPending}
                />
              ) : null}
              {booking.status === 'confirmed' ? (
                <ConfirmAction
                  label="Marcar completada"
                  onConfirm={() => completeMutation.mutate()}
                  pending={completeMutation.isPending}
                />
              ) : null}
              {managesFloor && (booking.status === 'held' || booking.status === 'confirmed') ? (
                <ChangeTable booking={booking} tables={tables} bookingId={bookingId} />
              ) : null}
            </section>
          ) : null}
          {booking.customerId && meetsPlan(business?.plan, minPlanFor('clientes')) && can('clientes') ? (
            <div className="section">
              <CustomerFile key={booking.customerId} customerId={booking.customerId} />
            </div>
          ) : null}
          {booking.events.length > 0 ? (
            <section aria-label="Historia" className="section">
              <h2 className="section-label">Historia</h2>
              <div className="rows">
                {booking.events.map((event, index) => (
                  <div className="row" key={`${event.createdAt}-${index}`}>
                    <p className="row-sub grow">
                      {BOOKING_STATUS_LABEL[event.toStatus]} {ACTOR_LABEL[event.actorType]}
                    </p>
                    <p className="row-sub">{formatDateTime(event.createdAt)}</p>
                  </div>
                ))}
              </div>
            </section>
          ) : null}
        </>
      ) : (
        <>
          <PageHeader title="Detalle de la reserva" />
          {detailQuery.isLoading ? <LoadingState label="Cargando reserva…" /> : null}
          {notFound ? <EmptyState title="No encontramos esa reserva." /> : null}
          {detailQuery.isError && !notFound ? (
            <ErrorBanner message={errorMessage(detailQuery.error)} />
          ) : null}
        </>
      )}
    </main>
  );
}

/** Move a live restaurant booking to another table that fits the party. */
function ChangeTable({
  booking,
  tables,
  bookingId,
}: {
  booking: BookingDetail;
  tables: BusinessTable[];
  bookingId: string;
}) {
  const queryClient = useQueryClient();
  const reassignKey = useRef(createIdempotencyKeyHolder()).current;
  const [targetId, setTargetId] = useState('');
  const [error, setError] = useState<string | undefined>();

  const candidates = tables.filter(
    (table) =>
      table.isActive &&
      table.id !== booking.tableId &&
      table.seats >= (booking.partySize ?? 1),
  );

  const reassignMutation = useMutation({
    mutationFn: () => reassignBookingTable(bookingId, targetId, reassignKey.ensure()),
    meta: { exito: 'Lugar cambiado' },
    onSuccess: () => {
      setTargetId('');
      setError(undefined);
      void queryClient.invalidateQueries({ queryKey: ['business-booking', bookingId] });
      void queryClient.invalidateQueries({ queryKey: ['business-bookings'] });
    },
    onError: (mutationError) => setError(errorMessage(mutationError)),
  });

  if (candidates.length === 0) return null;

  return (
    <div className="row">
      <div className="grow">
        <label className="row-sub" htmlFor="cambiar-mesa">
          Cambiar lugar
        </label>
        <select
          id="cambiar-mesa"
          value={targetId}
          onChange={(event) => setTargetId(event.target.value)}
        >
          <option value="">Elige un lugar</option>
          {candidates.map((table) => (
            <option key={table.id} value={table.id}>
              {table.name} · {table.seats}p · {table.zone}
            </option>
          ))}
        </select>
        {error ? <ErrorBanner message={error} /> : null}
      </div>
      <button
        type="button"
        className="btn small ghost"
        disabled={targetId === '' || reassignMutation.isPending}
        onClick={() => reassignMutation.mutate()}
      >
        {reassignMutation.isPending ? 'Cambiando…' : 'Cambiar'}
      </button>
    </div>
  );
}

function FactRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="row">
      <p className="row-sub">{label}</p>
      <p className="row-title">{children}</p>
    </div>
  );
}

/** The same facts the agenda row compresses into one line, each with its label. */
function bookingFactRows(booking: BookingDetail, business: ReturnType<typeof useBusiness>) {
  const rows: Array<[string, string]> = [
    ['Servicio', serviceName(business, booking.serviceId)],
    ['Duración', `${booking.durationMin} min`],
  ];
  if (booking.partySize !== undefined && booking.partySize > 1) {
    rows.push(['Personas', String(booking.partySize)]);
  }
  const staff = staffNameOf(booking, business);
  if (staff) rows.push(['Atiende', staff]);
  if (booking.offer) rows.push(['Oferta', booking.offer.title]);
  return rows.map(([label, value]) => (
    <FactRow key={label} label={label}>
      {value}
    </FactRow>
  ));
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'No pudimos cargar la reserva.';
}
